import { isDate } from 'date-fns';
import { SetURLSearchParams } from 'react-router-dom';
import { Severity, TAEntity } from './types';

export function extractGuid(str: string | null | undefined): string | undefined {
  if (typeof str === 'string') {
    const match = str.match(
      /[a-fA-F\d]{8}-[a-fA-F\d]{4}-[a-fA-F\d]{4}-[a-fA-F\d]{4}-[a-fA-F\d]{12}/i
    );
    return match ? match[0] : undefined;
  }
  return undefined;
}

export function isTAEntity(x: unknown): x is TAEntity {
  return typeof x === 'object' && !!x && (x as TAEntity).uid !== undefined;
}

// https://stackoverflow.com/questions/6860853/generate-random-string-for-div-id
export function generateGuid(): string {
  const S4 = () => (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
  return S4() + S4() + '-' + S4() + '-' + S4() + '-' + S4() + '-' + S4() + S4() + S4();
}

export function isEmptyGuid(param: string | undefined | null): boolean {
  if (
    param === undefined ||
    param === null ||
    param === '' ||
    param === '00000000-0000-0000-0000-000000000000'
  )
    return true;
  return false;
}

export function UpdateURL(url: string) {
  const newUrl = new URL(url, window.location.href).href;
  window.history.replaceState(null, document.title, newUrl);
}

export function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export function toArray<T>(val: T | T[]): T[] {
  return Array.isArray(val) ? val : [val];
}

// rc-field-form has a quirk with changedValues/allValues where if an element
// is removed from an array, allValues will still contain the element, but it
// will be a mostly empty object (so JSON.stringify will return '{}').  We want
// to fix that before sending down the pipeline
export function cleanFormValue(val: unknown): unknown {
  if (val && typeof val === 'object' && !isDate(val)) {
    if (Array.isArray(val)) {
      return val.filter(obj => !!obj && JSON.stringify(obj) !== '{}');
    } else {
      return Object.keys(val).reduce(
        (obj, key) => ({ ...obj, [key]: cleanFormValue(val[key]) }),
        {}
      );
    }
  }
  return val;
}

// This will find the longest word, and chunk the rest to that length
// This will produce the shortest possible chunks without breaking word or isolating short words unecessarily
export function chunckString(str: string, delimiter = ' '): string[] {
  const longestPart = str
    .split(delimiter)
    .reduce((longest, part) => (part.length > longest.length ? part : longest));
  return str.split(delimiter).reduce(
    (chunks, part) => {
      if (part === longestPart) {
        chunks.push(part);
      } else {
        if (chunks[chunks.length - 1].length >= longestPart.length) chunks.push('');
        if (chunks[chunks.length - 1].length > 0) chunks[chunks.length - 1] += delimiter;
        chunks[chunks.length - 1] += part;
      }
      return chunks;
    },
    ['']
  );
}

export function severityForThreatindex(threatindex: number): Severity {
  if (threatindex < 2) return Severity.informational;
  if (threatindex < 4) return Severity.low;
  if (threatindex < 7) return Severity.medium;
  if (threatindex <= 9) return Severity.high;
  return Severity.critical;
}

// These conversions were brought over from the old app, as instructed by Kevin on 10/25/2021
export function threatindexForSeverity(severity: Severity): number {
  switch (severity) {
    case Severity.informational:
      return 1;
    case Severity.low:
      return 2;
    case Severity.medium:
      return 4;
    case Severity.high:
      return 9;
    case Severity.critical:
      return 10;
  }
}

export function resizeIframeToContents(o) {
  o.target.style.height = o.target.contentWindow.document.body.parentElement.scrollHeight + 'px';
}

export function stringToEnum<T extends { [index: string]: string }>(
  enumValue: string,
  myEnum: T
): keyof T | null {
  const keys = Object.keys(myEnum).filter(x => myEnum[x] == enumValue);
  return keys.length > 0 ? keys[0] : null;
}

export function isEnumEqual<T>(enumObj: T, value: string, enumValue: T[keyof T]): boolean {
  return enumValue === enumObj[value];
}

export function setDifference<T>(setA: Set<T>, setB: Set<T>): Set<T> {
  const difference = new Set(setA);
  for (const elem of setB) {
    difference.delete(elem);
  }
  return difference;
}

//checks if an object has at least one property that is not null or undefined
export function isNotEmpty<T>(obj: T): boolean {
  if (obj === null || obj === undefined) return false;

  if (Object.keys(obj).length === 0) return false;

  for (const key in obj) {
    if (
      Object.prototype.hasOwnProperty.call(obj, key) &&
      obj[key] !== null &&
      obj[key] !== undefined
    ) {
      return true;
    }
  }

  return false;
}

export function addEditUrlParam(
  param: string,
  value: string,
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams
) {
  const newSearchParams = new URLSearchParams(searchParams.toString());

  if (newSearchParams.has(param)) {
    newSearchParams.set(param, value);
  } else {
    newSearchParams.append(param, value);
  }

  setSearchParams(newSearchParams);
}

export function removeUrlParam(
  param: string,
  searchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams
) {
  const newSearchParams = new URLSearchParams(searchParams.toString());

  newSearchParams.delete(param);

  setSearchParams(newSearchParams);
}
